import { ComponentProps } from 'react';

import useDC from '@eneco/dc/useDC';

import { useFlowHooks } from './FlowProvider';
import { getEventTypeByCategory, getNavigationCategories } from './FlowUtils';
import { NavigationBar } from '../components';
import { FlowContext, FlowRoute } from '../types/machine';

interface Props<T extends FlowContext> extends Pick<ComponentProps<typeof NavigationBar>, 'children'> {
  routes: FlowRoute<T>[];
  backLinkLabel: string | undefined;
  progressIndicatorLabels: {
    name: string;
    label: string;
  }[];
  showCategories?: boolean;
}

export const FlowNavigation = <T extends FlowContext = FlowContext>({
  backLinkLabel,
  progressIndicatorLabels,
  routes,
  children,
  showCategories = true,
}: Props<T>) => {
  const { label } = useDC();

  const { useFlowSelector, useFlowActorRef } = useFlowHooks<T>();
  const { send } = useFlowActorRef();
  const flowState = useFlowSelector(state => state);
  const flowContext = useFlowSelector(state => state.context);
  const currentCategory = useFlowSelector(state => state.context.pathCategory);
  const hasPreviousPath = useFlowSelector(state => state.context.hasPreviousPath);

  const shouldShowNavigationElements =
    !flowState.matches('INIT') && !flowState.matches('IDLE') && !flowState.matches('COMPLETED');
  const shouldShowPreviousButton = hasPreviousPath;

  const progressIndicatorItems = getNavigationCategories(routes);
  const progressIndicatorItemIndex = progressIndicatorItems.findIndex(item => item.category === currentCategory);

  return (
    <NavigationBar
      theme={label}
      backLink={
        shouldShowNavigationElements && shouldShowPreviousButton && backLinkLabel
          ? {
              label: backLinkLabel,
              onClick: () => send({ type: 'PREVIOUS_PAGE' }),
            }
          : undefined
      }
      progressIndicatorItems={
        showCategories && shouldShowNavigationElements
          ? progressIndicatorItems?.map((item, index) => {
              return {
                id: item.id,
                title: progressIndicatorLabels.find(label => label.name === item.category)?.label ?? '',
                onClick: () =>
                  send({
                    type: 'GOTO',
                    stepName: getEventTypeByCategory(routes, item.category, flowContext),
                  }),
                href: '#',
                isActive: progressIndicatorItemIndex === index,
                isDisabled: progressIndicatorItemIndex < index,
              };
            })
          : undefined
      }>
      {shouldShowNavigationElements ? children : null}
    </NavigationBar>
  );
};
